/**
 * Типы чата поддержки — зеркало DTO бэкенда
 * (TasteOk-Bakend/src/modules/support-chat/domain/types/support-chat.types.ts).
 * Все id — строки, даты — ISO: пейлоады общие для REST и socket.io.
 */

export type SupportTicketStatus = 'open' | 'in_progress' | 'closed'
export type SupportTicketType = 'payment' | 'delivery' | 'order_issue' | 'other'
export type SupportAuthor = 'user' | 'admin'

export interface SupportAttachment {
  key: string
  url: string
  mime: string
  size: number
  /** Имя файла — показывается для документов */
  name?: string
}

/** Документы: показываются карточкой со скачиванием, а не превью */
export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const

export const isDocumentAttachment = (attachment: SupportAttachment): boolean =>
  (DOCUMENT_MIME_TYPES as readonly string[]).includes(attachment.mime)

export const isVideoAttachment = (attachment: SupportAttachment): boolean =>
  attachment.mime.startsWith('video/')

/** Человекочитаемый размер файла для подписи под именем */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

/**
 * Реакция на сообщение. Ключ — СТОРОНА, а не конкретный человек: в диалоге 1:1
 * у клиента и у поддержки по одной реакции на сообщение (как в личке мессенджера).
 * «Моя» реакция вычисляется сравнением authorType со своей стороной.
 */
export interface SupportReaction {
  emoji: string
  authorType: SupportAuthor
}

/** Цитата: срез сообщения, на которое отвечают (вложения не дублируются) */
export interface SupportReplyPreview {
  id: string
  authorType: SupportAuthor
  text: string | null
  hasAttachments: boolean
  stickerId: string | null
}

export interface SupportMessage {
  id: string
  ticketId: string
  authorType: SupportAuthor
  text: string | null
  attachments: SupportAttachment[]
  orderId: string | null
  /** Сообщение, на которое отвечают; null — обычное */
  replyTo: SupportReplyPreview | null
  /** Стикер вместо текста: id вида pack.name (см. stickerPacks.ts) */
  stickerId: string | null
  /** Реакции сторон, максимум по одной на сторону */
  reactions: SupportReaction[]
  /** Доставлено на устройство получателя (вторая галочка) */
  deliveredAt: string | null
  /** Прочитано получателем (галочки меняют цвет) */
  readAt: string | null
  createdAt: string
  /** Локальный флаг: сообщение ещё отправляется (оптимистичный рендер) */
  pending?: boolean
}

/**
 * Быстрый ряд реакций в меню сообщения. Держим отдельно от бэкенда намеренно:
 * сервер принимает любой эмодзи, а этот список — просто «что под рукой».
 */
export const QUICK_REACTIONS = ['👍', '❤️', '🔥', '😂', '😮', '😢', '🙏'] as const

/** Лимит текста сообщения — как на сервере; держит и maxlength, и вставку эмодзи */
export const MESSAGE_MAX_LENGTH = 4000

/** Оценка обращения клиентом после закрытия */
export interface SupportRating {
  rating: number
  resolved: boolean | null
  fast: boolean | null
  comment: string | null
  ratedAt: string
}

export interface SupportTicket {
  id: string
  status: SupportTicketStatus
  type: SupportTicketType
  subject: string | null
  orderId: string | null
  unreadByUser: number
  unreadByAdmin: number
  lastMessageAt: string
  createdAt: string
  closedAt: string | null
  /** null — закрытое обращение ещё не оценено клиентом */
  rating: SupportRating | null
}

export interface AdminSupportTicket extends SupportTicket {
  user: { id: string, firstName: string | null, lastName: string | null, phone: string | null } | null
  appVersion: string | null
  clientPlatform: string | null
  deviceName: string | null
  lastMessagePreview: string | null
}

export interface SupportMessagesPage {
  messages: SupportMessage[]
  hasMore: boolean
  nextCursor: string | null
}

export interface SupportRecentOrder {
  id: string
  createdAt: string
  status: string
  paymentStatus: string
  totalAmount: number
  orderType: string
}

/** 360-контекст тикета для админки (GET /admin/support/tickets/:id) */
export interface SupportTicketContext {
  ticket: AdminSupportTicket
  client: {
    id: string
    firstName: string | null
    lastName: string | null
    phone: string | null
    email: string | null
    status: string
    isTest: boolean
    createdAt: string | null
    lastLoginAt: string | null
    points: number
    paidOrdersCount: number
    linkedAccounts: Array<{ id: string, firstName: string | null, lastName: string | null }>
    devices: Array<{ deviceName: string | null, platform: string | null, lastUsedAt: string | null }>
  }
  recentOrders: SupportRecentOrder[]
  attachedOrder: (SupportRecentOrder & {
    branchName: string | null
    deliveryAddress: string | null
    paymentMethod: string
    paymentProvider: string
    paymentInstrumentLabel: string | null
    externalPaymentId: string | null
    deliveryCost: number
    serviceFee: number
    appVersion: string | null
    items: Array<{ name: string, quantity: number, totalPrice: number, itemType: string }>
  }) | null
  paymentLogs: Array<{
    provider: 'alfabank' | 'tbusiness' | 'yookassa'
    event: string
    paymentStatus: string | null
    orderStatusBefore: string | null
    orderStatusAfter: string | null
    amount: number | null
    createdAt: string
  }>
  /** Последние ошибки клиента из GlitchTip */
  clientErrors: Array<{
    id: string
    title: string
    culprit: string | null
    count: number
    lastSeen: string
    level: string | null
    permalink: string | null
  }>
  /** false — интеграция с GlitchTip не настроена (не путать с «ошибок нет») */
  errorsAvailable: boolean
}

/** События socket.io чата (имена — контракт с бэкендом) */
export const SUPPORT_WS_EVENTS = {
  TICKET_NEW: 'support:ticket:new',
  MESSAGE_NEW: 'support:message:new',
  TICKET_UPDATED: 'support:ticket:updated',
  UNREAD_TOTAL: 'support:unread:total',
  MESSAGE_STATUS: 'support:message:status',
  MESSAGE_REACTION: 'support:message:reaction',
} as const

/** Реакции сообщения изменились: сервер присылает ПОЛНЫЙ актуальный набор */
export interface SupportMessageReactionEvent {
  ticketId: string
  messageId: string
  reactions: SupportReaction[]
}

/** Событие смены галочек: доставка по списку id, прочтение — сразу по всем своим */
export interface SupportMessageStatusEvent {
  ticketId: string
  status: 'delivered' | 'read'
  messageIds?: string[]
  allOwn?: boolean
}

export const TICKET_TYPE_LABELS: Record<SupportTicketType, string> = {
  payment: 'Не проходит оплата',
  delivery: 'Доставка',
  order_issue: 'Проблема с заказом',
  other: 'Другое',
}

export const TICKET_STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: 'Открыто',
  in_progress: 'В работе',
  closed: 'Закрыто',
}
